import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
    FaTimes,
    FaEnvelope,
    FaLock,
    FaUser,
    FaArrowRight,
    FaShieldAlt,
    FaCheckCircle,
} from "react-icons/fa";

export default function LoginModal({ isOpen, onClose, setUser }) {
    const [mode, setMode] = useState("login");
    const [errors, setErrors] = useState({});
    const [success, setSuccess] = useState("");
    const [loading, setLoading] = useState(false);

    const navigate = useNavigate();

    const [form, setForm] = useState({
        name: "",
        email: "",
        password: "",
    });

    if (!isOpen) return null;

    // =========================================================
    // HANDLE INPUT CHANGE
    // =========================================================
    const handleChange = (e) => {
        setForm({
            ...form,
            [e.target.name]: e.target.value,
        });

        setErrors({
            ...errors,
            [e.target.name]: "",
            general: "",
        });

        setSuccess("");
    };

    // =========================================================
    // VALIDATION
    // =========================================================
    const validate = () => {
        let newErrors = {};

        if (mode === "register" && !form.name.trim()) {
            newErrors.name = "Full name is required";
        }

        if (!form.email.trim()) {
            newErrors.email = "Email is required";
        }

        if (mode !== "forgot" && !form.password.trim()) {
            newErrors.password = "Password is required";
        }

        return newErrors;
    };

    // =========================================================
    // SUBMIT
    // =========================================================
    const handleSubmit = async (e) => {
        e.preventDefault();

        setErrors({});
        setSuccess("");

        const validationErrors = validate();

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setLoading(true);

        try {
            // =================================================
            // FORGOT PASSWORD
            // =================================================
            if (mode === "forgot") {
                await axios.post(
                    "https://marinadubai.com/api/forgot-password",
                    {
                        email: form.email,
                    }
                );

                setSuccess(
                    "If an account exists with this email, a password reset link has been sent."
                );

                setLoading(false);
                return;
            }

            // =================================================
            // REGISTER
            // =================================================
            if (mode === "register") {
                await axios.post(
                    "https://marinadubai.com/api/register",
                    {
                        name: form.name,
                        email: form.email,
                        password: form.password,
                        password_confirmation: form.password,
                    }
                );

                alert("User registered successfully");

                setForm({
                    name: "",
                    email: "",
                    password: "",
                });

                setMode("login");
                setLoading(false);

                return;
            }

            // =================================================
            // LOGIN
            // =================================================
            if (mode === "login") {
                const res = await axios.post(
                    "https://marinadubai.com/api/login",
                    {
                        email: form.email,
                        password: form.password,
                    }
                );

                const userData = res.data.user;

                // Save user
                localStorage.setItem(
                    "user",
                    JSON.stringify(userData)
                );

                setUser(userData);

                alert("Login successful");

                setLoading(false);

                // Close modal
                onClose();

                // Redirect
                navigate("/profile");
            }
        } catch (err) {
            console.error(err);

            setLoading(false);

            if (mode === "forgot") {
                setErrors({
                    general:
                        err.response?.data?.message ||
                        "Unable to send the reset link. Please try again later.",
                });
            } else {
                setErrors({
                    general:
                        err.response?.data?.message ||
                        "Authentication failed. Please check your details.",
                });
            }
        }
    };

    // =========================================================
    // SWITCH TO LOGIN
    // =========================================================
    const goToLogin = () => {
        setMode("login");
        setErrors({});
        setSuccess("");

        setForm({
            name: "",
            email: form.email,
            password: "",
        });
    };

    // =========================================================
    // SWITCH TO REGISTER
    // =========================================================
    const goToRegister = () => {
        setMode("register");
        setErrors({});
        setSuccess("");
    };

    // =========================================================
    // SWITCH TO FORGOT PASSWORD
    // =========================================================
    const goToForgotPassword = () => {
        setMode("forgot");
        setErrors({});
        setSuccess("");

        setForm({
            name: "",
            email: form.email,
            password: "",
        });
    };

    return (
        <div
            className="
                fixed inset-0 z-50
                flex items-center justify-center
                bg-[#173F3E]/75
                px-4
                py-6
                backdrop-blur-md
            "
            onClick={onClose}
        >
            {/* =====================================================
                MODAL
            ===================================================== */}
            <div
                className="
                    relative
                    w-full
                    max-w-[470px]
                    overflow-hidden
                    rounded-[30px]
                    border
                    border-[#E7DED0]
                    bg-[#FBFAF6]
                    shadow-[0_30px_100px_rgba(23,63,62,0.30)]
                "
                onClick={(e) => e.stopPropagation()}
            >

                {/* =================================================
                    TOP GOLD ACCENT
                ================================================= */}
                <div className="h-1 w-full bg-gradient-to-r from-[#B78932] via-[#D5A33C] to-[#B78932]" />


                {/* =================================================
                    DECORATIVE BACKGROUND
                ================================================= */}
                <div
                    className="
                        pointer-events-none
                        absolute
                        -right-24
                        -top-24
                        h-56
                        w-56
                        rounded-full
                        bg-[#0B5552]/10
                        blur-3xl
                    "
                />

                <div
                    className="
                        pointer-events-none
                        absolute
                        -bottom-24
                        -left-24
                        h-56
                        w-56
                        rounded-full
                        bg-[#B78932]/10
                        blur-3xl
                    "
                />

                {/* =================================================
                    CLOSE BUTTON
                ================================================= */}
                <button
                    onClick={onClose}
                    type="button"
                    aria-label="Close"
                    className="
                        absolute
                        right-5
                        top-5
                        z-30
                        flex
                        h-10
                        w-10
                        items-center
                        justify-center
                        rounded-full
                        border
                        border-[#E7DED0]
                        bg-white/80
                        text-[#173F3E]
                        shadow-sm
                        backdrop-blur
                        transition-all
                        duration-300
                        hover:border-[#B78932]
                        hover:bg-[#F3EBDD]
                        hover:text-[#0B5552]
                        hover:rotate-90
                    "
                >
                    <FaTimes className="text-sm" />
                </button>


                {/* =================================================
                    MAIN CONTENT
                ================================================= */}
                <div className="relative z-10 p-6 sm:p-9">


                    {/* =================================================
                        BRAND / ICON
                    ================================================= */}
                    <div className="text-center">

                        <div
                            className="
                                relative
                                mx-auto
                                flex
                                h-16
                                w-16
                                items-center
                                justify-center
                                rounded-[20px]
                                border
                                border-[#D5A33C]/30
                                bg-[#F3EBDD]
                                text-[#0B5552]
                                shadow-[0_8px_25px_rgba(183,137,50,0.10)]
                            "
                        >
                            {/* Gold decorative corner */}
                            <span
                                className="
                                    absolute
                                    -right-1
                                    -top-1
                                    h-3
                                    w-3
                                    rounded-full
                                    bg-[#B78932]
                                "
                            />

                            {mode === "register" ? (
                                <FaUser className="text-xl" />
                            ) : mode === "forgot" ? (
                                <FaEnvelope className="text-xl" />
                            ) : (
                                <FaLock className="text-xl" />
                            )}
                        </div>


                        {/* =================================================
                            EYEBROW
                        ================================================= */}
                        <div
                            className="
                                mx-auto
                                mt-5
                                inline-flex
                                items-center
                                gap-2
                                text-[10px]
                                font-bold
                                uppercase
                                tracking-[0.2em]
                                text-[#0B5552]
                            "
                        >
                            <span className="h-px w-6 bg-[#B78932]" />

                            {mode === "register"
                                ? "Join Dubai Marina"
                                : mode === "forgot"
                                    ? "Account Recovery"
                                    : "Welcome Back"}

                            <span className="h-px w-6 bg-[#B78932]" />
                        </div>


                        {/* =================================================
                            TITLE
                        ================================================= */}
                        <h2
                            className="
                                mt-4
                                font-serif
                                text-3xl
                                font-medium
                                tracking-tight
                                text-[#173F3E]
                                sm:text-[34px]
                            "
                        >
                            {mode === "register"
                                ? "Create Your Account"
                                : mode === "forgot"
                                    ? "Reset Your Password"
                                    : "Sign In"}
                        </h2>


                        {/* =================================================
                            SUBTITLE
                        ================================================= */}
                        <p
                            className="
                                mx-auto
                                mt-2
                                max-w-sm
                                text-sm
                                leading-6
                                text-[#66736F]
                            "
                        >
                            {mode === "register"
                                ? "Create your account and make managing your Dubai stays easier."
                                : mode === "forgot"
                                    ? "Enter your email and we'll send you a secure password reset link."
                                    : "Sign in to manage your hotel bookings and reservations."}
                        </p>

                    </div>


                    {/* =================================================
                        GENERAL ERROR
                    ================================================= */}
                    {errors.general && (
                        <div
                            className="
                                mt-6
                                flex
                                items-start
                                gap-3
                                rounded-2xl
                                border
                                border-red-200
                                bg-red-50
                                px-4
                                py-3.5
                                text-sm
                                leading-5
                                text-red-600
                            "
                        >
                            <span
                                className="
                                    mt-1
                                    h-2
                                    w-2
                                    shrink-0
                                    rounded-full
                                    bg-red-500
                                "
                            />

                            <span>{errors.general}</span>
                        </div>
                    )}


                    {/* =================================================
                        SUCCESS MESSAGE
                    ================================================= */}
                    {success && (
                        <div
                            className="
                                mt-6
                                flex
                                items-start
                                gap-3
                                rounded-2xl
                                border
                                border-emerald-200
                                bg-emerald-50
                                px-4
                                py-3.5
                                text-sm
                                leading-5
                                text-emerald-700
                            "
                        >
                            <FaCheckCircle className="mt-0.5 shrink-0" />

                            <span>{success}</span>
                        </div>
                    )}


                    {/* =================================================
                        FORM
                    ================================================= */}
                    <form
                        onSubmit={handleSubmit}
                        className="mt-7 space-y-5"
                    >

                        {/* =================================================
                            NAME
                        ================================================= */}
                        {mode === "register" && (
                            <div>

                                <label
                                    htmlFor="name"
                                    className="
                                        mb-2
                                        block
                                        text-[11px]
                                        font-bold
                                        uppercase
                                        tracking-[0.12em]
                                        text-[#173F3E]
                                    "
                                >
                                    Full Name
                                </label>

                                <div className="relative">

                                    <FaUser
                                        className="
                                            absolute
                                            left-4
                                            top-1/2
                                            -translate-y-1/2
                                            text-xs
                                            text-[#9A9E97]
                                        "
                                    />

                                    <input
                                        id="name"
                                        name="name"
                                        value={form.name}
                                        placeholder="Enter your full name"
                                        onChange={handleChange}
                                        className="
                                            w-full
                                            rounded-2xl
                                            border
                                            border-[#DED8CC]
                                            bg-white
                                            py-3.5
                                            pl-11
                                            pr-4
                                            text-sm
                                            text-[#173F3E]
                                            outline-none
                                            transition-all
                                            duration-300
                                            placeholder:text-[#A2A59F]
                                            focus:border-[#0B5552]
                                            focus:bg-[#FBFAF6]
                                            focus:ring-4
                                            focus:ring-[#0B5552]/10
                                        "
                                    />

                                </div>

                                {errors.name && (
                                    <p className="mt-1.5 text-xs text-red-500">
                                        {errors.name}
                                    </p>
                                )}

                            </div>
                        )}


                        {/* =================================================
                            EMAIL
                        ================================================= */}
                        <div>

                            <label
                                htmlFor="email"
                                className="
                                    mb-2
                                    block
                                    text-[11px]
                                    font-bold
                                    uppercase
                                    tracking-[0.12em]
                                    text-[#173F3E]
                                "
                            >
                                Email Address
                            </label>

                            <div className="relative">

                                <FaEnvelope
                                    className="
                                        absolute
                                        left-4
                                        top-1/2
                                        -translate-y-1/2
                                        text-xs
                                        text-[#9A9E97]
                                    "
                                />

                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    value={form.email}
                                    placeholder="Enter your email"
                                    onChange={handleChange}
                                    className="
                                        w-full
                                        rounded-2xl
                                        border
                                        border-[#DED8CC]
                                        bg-white
                                        py-3.5
                                        pl-11
                                        pr-4
                                        text-sm
                                        text-[#173F3E]
                                        outline-none
                                        transition-all
                                        duration-300
                                        placeholder:text-[#A2A59F]
                                        focus:border-[#0B5552]
                                        focus:bg-[#FBFAF6]
                                        focus:ring-4
                                        focus:ring-[#0B5552]/10
                                    "
                                />

                            </div>

                            {errors.email && (
                                <p className="mt-1.5 text-xs text-red-500">
                                    {errors.email}
                                </p>
                            )}

                        </div>


                        {/* =================================================
                            PASSWORD
                        ================================================= */}
                        {mode !== "forgot" && (
                            <div>

                                <label
                                    htmlFor="password"
                                    className="
                                        mb-2
                                        block
                                        text-[11px]
                                        font-bold
                                        uppercase
                                        tracking-[0.12em]
                                        text-[#173F3E]
                                    "
                                >
                                    Password
                                </label>

                                <div className="relative">

                                    <FaLock
                                        className="
                                            absolute
                                            left-4
                                            top-1/2
                                            -translate-y-1/2
                                            text-xs
                                            text-[#9A9E97]
                                        "
                                    />

                                    <input
                                        id="password"
                                        name="password"
                                        type="password"
                                        value={form.password}
                                        placeholder="Enter your password"
                                        onChange={handleChange}
                                        className="
                                            w-full
                                            rounded-2xl
                                            border
                                            border-[#DED8CC]
                                            bg-white
                                            py-3.5
                                            pl-11
                                            pr-4
                                            text-sm
                                            text-[#173F3E]
                                            outline-none
                                            transition-all
                                            duration-300
                                            placeholder:text-[#A2A59F]
                                            focus:border-[#0B5552]
                                            focus:bg-[#FBFAF6]
                                            focus:ring-4
                                            focus:ring-[#0B5552]/10
                                        "
                                    />

                                </div>

                                {errors.password && (
                                    <p className="mt-1.5 text-xs text-red-500">
                                        {errors.password}
                                    </p>
                                )}


                                {/* Forgot Password */}
                                {mode === "login" && (
                                    <div className="mt-3 flex justify-end">

                                        <button
                                            type="button"
                                            onClick={goToForgotPassword}
                                            className="
                                                text-xs
                                                font-bold
                                                text-[#0B5552]
                                                transition
                                                hover:text-[#B78932]
                                            "
                                        >
                                            Forgot Password?
                                        </button>

                                    </div>
                                )}

                            </div>
                        )}


                        {/* =================================================
                            SUBMIT BUTTON
                        ================================================= */}
                        <button
                            type="submit"
                            disabled={loading}
                            className="
                                group
                                relative
                                flex
                                w-full
                                items-center
                                justify-center
                                gap-3
                                overflow-hidden
                                rounded-2xl
                                bg-[#173F3E]
                                px-5
                                py-4
                                text-sm
                                font-bold
                                text-white
                                shadow-[0_12px_30px_rgba(23,63,62,0.18)]
                                transition-all
                                duration-300
                                hover:-translate-y-0.5
                                hover:bg-[#0B5552]
                                hover:shadow-[0_16px_35px_rgba(23,63,62,0.25)]
                                disabled:cursor-not-allowed
                                disabled:opacity-60
                            "
                        >

                            {/* Gold hover line */}
                            <span
                                className="
                                    absolute
                                    bottom-0
                                    left-0
                                    h-1
                                    w-0
                                    bg-[#B78932]
                                    transition-all
                                    duration-500
                                    group-hover:w-full
                                "
                            />

                            {loading ? (
                                <>
                                    <span
                                        className="
                                            h-5
                                            w-5
                                            animate-spin
                                            rounded-full
                                            border-2
                                            border-white/30
                                            border-t-white
                                        "
                                    />

                                    Please wait...
                                </>
                            ) : (
                                <>
                                    {mode === "register"
                                        ? "Create Account"
                                        : mode === "forgot"
                                            ? "Send Reset Link"
                                            : "Sign In"}

                                    <FaArrowRight
                                        className="
                                            text-xs
                                            transition-transform
                                            duration-300
                                            group-hover:translate-x-1
                                        "
                                    />
                                </>
                            )}

                        </button>

                    </form>


                    {/* =================================================
                        BOTTOM NAVIGATION
                    ================================================= */}
                    <div className="mt-6 text-center text-sm text-[#69736F]">

                        {mode === "login" && (
                            <>
                                Don't have an account?{" "}

                                <button
                                    type="button"
                                    onClick={goToRegister}
                                    className="
                                        font-bold
                                        text-[#0B5552]
                                        transition
                                        hover:text-[#B78932]
                                    "
                                >
                                    Create account
                                </button>
                            </>
                        )}


                        {mode === "register" && (
                            <>
                                Already have an account?{" "}

                                <button
                                    type="button"
                                    onClick={goToLogin}
                                    className="
                                        font-bold
                                        text-[#0B5552]
                                        transition
                                        hover:text-[#B78932]
                                    "
                                >
                                    Sign in
                                </button>
                            </>
                        )}


                        {mode === "forgot" && (
                            <button
                                type="button"
                                onClick={goToLogin}
                                className="
                                    inline-flex
                                    items-center
                                    gap-2
                                    font-bold
                                    text-[#0B5552]
                                    transition
                                    hover:text-[#B78932]
                                "
                            >
                                ← Back to login
                            </button>
                        )}

                    </div>


                    {/* =================================================
                        SECURITY NOTE
                    ================================================= */}
                    <div
                        className="
                            mt-7
                            flex
                            items-center
                            justify-center
                            gap-2
                            border-t
                            border-[#E7DED0]
                            pt-5
                            text-[10px]
                            font-bold
                            uppercase
                            tracking-[0.15em]
                            text-[#9A9E97]
                        "
                    >
                        <FaShieldAlt className="text-[#B78932]" />

                        Secure & Protected
                    </div>

                </div>

            </div>

        </div>
    );
}